import type { z } from "zod";
import { hashString } from "../utils/hashString";
import { borrowAccessToken, invalidateAccessToken } from "./borrowApiKey";
import { createCacheRequest, getCache, parseCachedResponse } from "./cache";
import { getApiUrl } from "./config";
import { ApiError, requestJson } from "./requestJson";

interface BackendRequestOptions<T> {
  body: unknown;
  responseSchema: z.ZodType<T>;
  signal?: AbortSignal;
  cacheNamespace?: string;
}

export async function generateBackendCacheKey(
  path: string,
  body: unknown,
  cacheNamespace: string,
): Promise<string> {
  return await hashString(JSON.stringify({ version: 1, cacheNamespace, path, body }));
}

export async function requestBackend<T>(
  path: string,
  { body, responseSchema, signal, cacheNamespace }: BackendRequestOptions<T>,
): Promise<T> {
  const cacheKey = cacheNamespace
    ? await generateBackendCacheKey(path, body, cacheNamespace)
    : null;

  if (cacheKey) {
    const cached = await readCache(cacheKey, responseSchema);
    if (cached !== null) return cached;
  }

  let token = await borrowAccessToken(signal);
  let result: T;
  try {
    result = await sendRequest(path, body, responseSchema, token, signal);
  } catch (error) {
    if (!(error instanceof ApiError) || error.status !== 401) throw error;
    invalidateAccessToken(token);
    token = await borrowAccessToken(signal);
    result = await sendRequest(path, body, responseSchema, token, signal);
  }

  if (cacheKey) await writeCache(cacheKey, result);
  return result;
}

async function sendRequest<T>(
  path: string,
  body: unknown,
  responseSchema: z.ZodType<T>,
  token: string,
  signal?: AbortSignal,
): Promise<T> {
  return await requestJson(getApiUrl(path), {
    method: "POST",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
    mode: "cors",
    signal,
    validate: (value): value is T => responseSchema.safeParse(value).success,
  });
}

async function readCache<T>(cacheKey: string, responseSchema: z.ZodType<T>): Promise<T | null> {
  const cache = await getCache();
  if (!cache) return null;
  const request = createCacheRequest(cacheKey);
  try {
    const result = await parseCachedResponse(await cache.match(request), responseSchema);
    if (result.status === "hit") return result.data;
    if (result.status === "expired" || result.status === "invalid") {
      await cache.delete(request);
    }
  } catch (error) {
    console.debug("Failed to read from cache:", error);
  }
  return null;
}

async function writeCache<T>(cacheKey: string, data: T): Promise<void> {
  const cache = await getCache();
  if (!cache) return;
  try {
    await cache.put(
      createCacheRequest(cacheKey),
      new Response(JSON.stringify(data), {
        headers: {
          "Content-Type": "application/json",
          "Cache-Key": cacheKey,
          "Cached-At": new Date().toISOString(),
        },
      }),
    );
  } catch (error) {
    console.debug("Failed to store in cache:", error);
  }
}
